import React from 'react';
import { Modal, Button, Badge } from '@tracekit/core';
import { Clock, ExternalLink, Rss } from 'lucide-react';
import { RSSFeedItem } from '../services/usGovernmentIntegrations';
import { stripHtml } from '../utils/htmlToPlainText';
import {
  formatPublishedAt,
  getFeedSourceDisplayName,
  resolveRssFeedSource,
} from '../utils/industryNewsFormatting';

interface IndustryNewsArticleModalProps {
  item: RSSFeedItem | null;
  isOpen: boolean;
  onClose: () => void;
}

export function IndustryNewsArticleModal({
  item,
  isOpen,
  onClose,
}: IndustryNewsArticleModalProps) {
  if (!item) {
    return null;
  }

  const feedSource = resolveRssFeedSource(item);
  const sourceName = getFeedSourceDisplayName(item, feedSource);
  const description = stripHtml(item.description);

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={item.title}
      size="lg"
      bodyClassName="max-h-[60vh] overflow-y-auto"
      footer={
        <div className="flex w-full items-center justify-between gap-3">
          <Button variant="outline" onClick={onClose}>
            Close
          </Button>
          <Button
            variant="primary"
            onClick={() => window.open(item.link, '_blank', 'noopener,noreferrer')}
          >
            <ExternalLink className="mr-2 h-4 w-4" />
            Open original
          </Button>
        </div>
      }
    >
      <div className="space-y-4">
        <div className="flex flex-wrap items-center gap-2">
          <Badge variant="primary" className="flex items-center gap-1 text-xs">
            <Rss className="h-3 w-3" />
            {sourceName}
          </Badge>
          <span
            className="flex items-center gap-1 text-sm text-gray-500 dark:text-gray-400"
            title={formatPublishedAt(item.pubDate)}
          >
            <Clock className="h-3.5 w-3.5" />
            {formatPublishedAt(item.pubDate)}
          </span>
        </div>

        {description ? (
          <p className="whitespace-pre-wrap text-sm leading-relaxed text-gray-700 dark:text-gray-300">
            {description}
          </p>
        ) : (
          <p className="text-sm italic text-gray-500 dark:text-gray-400">
            No description available for this article.
          </p>
        )}
      </div>
    </Modal>
  );
}
